/**
 * SEO Utility Functions
 * Provides helper functions for generating SEO meta tags, structured data, and canonical URLs
 */

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://dresez.com";
const DEFAULT_DESCRIPTION =
  "Sell what you don’t wear, find what you want to. Anyone can post, anyone can shop — clothes, shoes, accessories. Worldwide marketplace.";
const DEFAULT_TITLE = "dresez — Sell what you don’t wear";
const DEFAULT_IMAGE = `${BASE_URL}/apple-touch-icon.png`;

/**
 * Generate canonical URL for a page
 */
export const getCanonicalUrl = (path = "") => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${BASE_URL}${cleanPath}`;
};

/**
 * Generate alternate language URLs (hreflang)
 * Multilingual URL routing has been removed; kept as a no-op for callers.
 */
export const getHreflangTags = () => [];

/**
 * Generate Open Graph meta tags
 */
export const getOpenGraphTags = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url = BASE_URL,
  type = "website",
  siteName = "dresez",
  locale = "en_US",
}) => {
  return [
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: image },
    { property: "og:url", content: url },
    { property: "og:type", content: type },
    { property: "og:site_name", content: siteName },
    { property: "og:locale", content: locale },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: title },
  ];
};

/**
 * Generate Twitter Card meta tags
 */
export const getTwitterCardTags = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  card = "summary_large_image",
}) => {
  return [
    { name: "twitter:card", content: card },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: title },
  ];
};

/**
 * Generate structured data (JSON-LD) for a product/post
 */
export const getProductStructuredData = ({
  title,
  description,
  price,
  currency = "USD",
  images = [],
  condition = "new",
  availability = "https://schema.org/InStock",
  url,
  sellerName = "dresez Seller",
  category = "Fashion",
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    description: description,
    image: images.length > 0 ? images : [DEFAULT_IMAGE],
    offers: {
      "@type": "Offer",
      price: price || "0",
      priceCurrency: currency,
      availability: availability,
      itemCondition: `https://schema.org/${
        condition === "new" ? "NewCondition" : "UsedCondition"
      }`,
    },
    brand: {
      "@type": "Brand",
      name: "dresez",
    },
    category: category,
    seller: {
      "@type": "Organization",
      name: sellerName,
    },
    url: url,
  };
};

/**
 * Generate structured data for organization
 */
export const getOrganizationStructuredData = ({
  name = "dresez",
  url = BASE_URL,
  logo = `${BASE_URL}/android-chrome-512x512.png`,
  description = DEFAULT_DESCRIPTION,
  sameAs = [], // Social media links
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: name,
    url: url,
    logo: logo,
    description: description,
    sameAs: sameAs,
  };
};

/**
 * Generate structured data for breadcrumbs
 */
export const getBreadcrumbStructuredData = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

/**
 * Generate structured data for a web page
 */
export const getWebPageStructuredData = ({ name, description, url, image }) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: name,
    description: description,
    url: url,
    image: image,
  };
};

/**
 * Get locale code for Open Graph
 */
export const getLocaleCode = (language) => {
  const localeMap = {
    en: "en_US",
    id: "id_ID",
    es: "es_ES",
    "pt-BR": "pt_BR",
    hi: "hi_IN",
    ur: "ur_PK",
    fr: "fr_FR",
    de: "de_DE",
  };
  return localeMap[language] || "en_US";
};

/**
 * Generate complete SEO meta tags object for Helmet
 *
 * Note: the `country`, `language`, and `alternateLanguages` arguments are kept
 * in the signature for caller compatibility but are no longer used for URL
 * construction (we removed the `/:country/:language/` URL prefix). `language`
 * still informs the Open Graph locale.
 */
export const generateSEOTags = ({
  title,
  description,
  image,
  url,
  type = "website",
  // eslint-disable-next-line no-unused-vars
  country,
  language = "en",
  keywords = [],
  noindex = false,
  nofollow = false,
  canonical,
  // eslint-disable-next-line no-unused-vars
  alternateLanguages = [],
}) => {
  const fullTitle = title ? `${title} — dresez` : DEFAULT_TITLE;
  const fullDescription = description || DEFAULT_DESCRIPTION;
  const fullImage = image || DEFAULT_IMAGE;
  const canonicalUrl = canonical || url || BASE_URL;
  const locale = getLocaleCode(language);

  const metaTags = [
    // Basic meta tags
    { name: "description", content: fullDescription },
    {
      name: "keywords",
      content:
        keywords.join(", ") ||
        "fashion, designer, buy, sell, pre-loved, clothing, accessories",
    },
    { name: "author", content: "dresez" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },

    // Robots
    ...(noindex || nofollow
      ? [
          {
            name: "robots",
            content: `${noindex ? "noindex" : "index"}, ${
              nofollow ? "nofollow" : "follow"
            }`,
          },
        ]
      : []),

    // Open Graph
    ...getOpenGraphTags({
      title: fullTitle,
      description: fullDescription,
      image: fullImage,
      url: canonicalUrl,
      type,
      locale,
    }),

    // Twitter Card
    ...getTwitterCardTags({
      title: fullTitle,
      description: fullDescription,
      image: fullImage,
    }),
  ];

  const linkTags = [{ rel: "canonical", href: canonicalUrl }];

  return {
    title: fullTitle,
    meta: metaTags,
    link: linkTags,
  };
};
